import { useMemo } from 'react'
import { Badge, Box, Center, Container, Group, Loader, Select, Stack, Tabs, Text, Title } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import type { UserActivityItemDto, UserShort } from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import { fetchUserActivity } from '../shared/api/activity'
import { fetchUserAudit, fetchUsers } from '../shared/api/users'

/**
 * #7 слой 3 — журнал действий сотрудника. ОТДЕЛЬНАЯ страница под audit.view (НЕ
 * /admin/users — там role==='admin', делегированное право было бы мёртвым, Р-C).
 * Две секции на чтении (Р4): «Активность» (ActivityEvent по actorId) + «Аудит»
 * (UserAuditEvent — кадровые изменения). Держатель audit.view переключает сотрудника
 * селектором; обычный пользователь видит только себя (self, Р5).
 */
export function UserActivityLogPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const targetId = id ?? user?.id ?? ''
  const canAudit = user?.permissions.includes('audit.view') ?? false
  const isSelf = !!user && user.id === targetId
  const allowed = isSelf || canAudit

  // Селектор сотрудников — только держателям audit.view (директор проходит GET /users).
  const usersQ = useQuery({ queryKey: ['users-for-log'], queryFn: () => fetchUsers(), enabled: canAudit })

  const activityQ = useQuery({
    queryKey: ['user-activity', targetId],
    queryFn: () => fetchUserActivity(targetId),
    enabled: allowed && !!targetId,
  })
  const auditQ = useQuery({
    queryKey: ['user-audit-log', targetId],
    queryFn: () => fetchUserAudit(targetId),
    enabled: allowed && !!targetId,
  })

  const userById = useMemo(() => {
    const m = new Map<string, UserShort>()
    for (const u of activityQ.data?.users ?? []) m.set(u.id, u)
    return m
  }, [activityQ.data])

  if (!allowed) {
    return (
      <AppLayout>
        <Container size="md" py="md"><Text c="red" mt="md">Нет доступа к журналу действий этого сотрудника.</Text></Container>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <Container size="md" py="md">
        <Group justify="space-between" mb="md" align="flex-end">
          <div>
            <Text size="xs" c="grape.7" fw={700} tt="uppercase">Аудит</Text>
            <Title order={3}>Журнал действий</Title>
          </div>
          {canAudit && (
            <Select
              label="Сотрудник"
              w={260}
              searchable
              value={targetId}
              data={(usersQ.data ?? []).map((u) => ({ value: u.id, label: u.full_name }))}
              onChange={(v) => v && navigate(`/people/${v}/log`)}
            />
          )}
        </Group>

        <Tabs defaultValue="activity">
          <Tabs.List mb="sm">
            <Tabs.Tab value="activity">Активность</Tabs.Tab>
            <Tabs.Tab value="audit">Аудит (кадровое)</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="activity">
            {activityQ.isLoading ? (
              <Center py="xl"><Loader size="sm" /></Center>
            ) : (activityQ.data?.events.length ?? 0) === 0 ? (
              <Text size="sm" c="dimmed">Действий пока нет.</Text>
            ) : (
              <Stack gap={4}>
                {activityQ.data!.events.map((e) => <ActivityRow key={e.id} e={e} userById={userById} />)}
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="audit">
            {auditQ.isLoading ? (
              <Center py="xl"><Loader size="sm" /></Center>
            ) : (auditQ.data?.length ?? 0) === 0 ? (
              <Text size="sm" c="dimmed">Кадровых изменений нет.</Text>
            ) : (
              <Stack gap={4}>
                {auditQ.data!.map((e) => (
                  <Box key={e.id} p="xs" style={{ borderLeft: '2px solid var(--mantine-color-blue-3)', background: 'var(--mantine-color-gray-0)' }}>
                    <Group gap={6}>
                      <Badge size="xs" variant="light">{e.event_type}</Badge>
                      <Text size="xs" c="dimmed">{new Date(e.created_at).toLocaleString('ru-RU')}</Text>
                      <Text size="xs" c="dimmed">· {e.actor?.full_name ?? 'система'}</Text>
                    </Group>
                    {(e.from_value != null || e.to_value != null) && (
                      <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', marginTop: 2 }}>
                        {e.from_value != null ? `${JSON.stringify(e.from_value)} → ` : ''}
                        {e.to_value != null ? JSON.stringify(e.to_value) : ''}
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Container>
    </AppLayout>
  )
}

// Человекочитаемые ярлыки событий ленты (#7 слой 4 — мутации проекта/членов/контактов
// + ключевые task-события). Неизвестный тип → сырой ключ (не падаем).
const EVENT_LABELS: Record<string, string> = {
  task_created: 'создал задачу',
  title_changed: 'изменил заголовок',
  description_changed: 'изменил описание',
  status_changed: 'сменил статус задачи',
  started: 'взял в работу',
  completed: 'завершил задачу',
  reopened: 'переоткрыл задачу',
  closed: 'закрыл задачу',
  assignee_changed: 'сменил исполнителя',
  due_changed: 'изменил срок',
  priority_changed: 'изменил приоритет',
  comment_added: 'добавил комментарий',
  tg_draft_confirmed: 'принял задачу из Telegram',
  tg_draft_rejected: 'отклонил черновик из Telegram',
  project_created: 'создал проект',
  project_updated: 'изменил проект',
  project_status_changed: 'сменил статус проекта',
  project_archived: 'архивировал проект',
  member_added: 'добавил участника',
  member_role_changed: 'изменил роль участника',
  member_removed: 'удалил участника',
  contact_added: 'добавил контакт',
  contact_updated: 'изменил контакт',
  contact_removed: 'удалил контакт',
}
const eventLabel = (t: string) => EVENT_LABELS[t] ?? t

function ActivityRow({ e, userById }: { e: UserActivityItemDto; userById: Map<string, UserShort> }) {
  // Резолв user_id/assignee_id из from/to в имена (справочник users из ответа).
  const resolve = (v: unknown): string => {
    if (v == null) return ''
    if (typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>
      for (const k of ['user_id', 'assignee_id']) {
        const ref = o[k]
        if (typeof ref === 'string') return userById.get(ref)?.full_name ?? ref
      }
    }
    return JSON.stringify(v)
  }
  return (
    <Box p="xs" style={{ borderLeft: '2px solid var(--mantine-color-teal-3)', background: 'var(--mantine-color-gray-0)' }}>
      <Group gap={6}>
        <Badge size="xs" variant="light" color="teal">{eventLabel(e.event_type)}</Badge>
        <Text size="xs" c="dimmed">{new Date(e.created_at).toLocaleString('ru-RU')}</Text>
        <Text size="xs" c="dimmed">· {e.project.name}{e.task ? ` · #${e.task.human_id} ${e.task.title}` : ''}</Text>
      </Group>
      {(e.from_value != null || e.to_value != null) && (
        <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', marginTop: 2 }}>
          {e.from_value != null ? `${resolve(e.from_value)} → ` : ''}
          {e.to_value != null ? resolve(e.to_value) : ''}
        </Text>
      )}
    </Box>
  )
}
